import { Handle, Position } from "@xyflow/react";
import { Layers } from "lucide-react";
import type { System } from "@/lib/arch-parser";
import { cn } from "@/lib/utils";
import { useArchViz } from "../ArchVizContext";
import { CommentBadge } from "./CommentBadge";
import { RiskBadge } from "./RiskBadge";

export interface SystemNodeData {
  system: System;
  // Free text from meta.lifecycle_stage. Rendered as a small chip when set.
  lifecycleStage?: string;
  containerCount: number;
  // Aggregated risk count across containers + components + externals — same
  // signal SystemBoundaryNode shows. The badge opens the risks sheet.
  aggregatedRiskCount?: number;
  containersWithRiskCount?: number;
  // Decorated by GraphCanvas; same as other nodes.
  commentCount?: number;
  carryOverCount?: number;
  [key: string]: unknown;
}

const MAX_SUBTITLE_LEN = 80;

// Subtitle prefers the curated headline; falls back to first sentence of
// description, truncated at MAX_SUBTITLE_LEN on a word boundary with an
// ellipsis. Same shape as scripts/backfill-headlines.ts so existing curated
// headlines and on-the-fly derivations look consistent.
function subtitleFor(system: System): string {
  const headline = system.headline?.trim();
  if (headline && headline.length > 0) return headline;
  const desc = system.description?.trim();
  if (!desc) return "";
  const firstSentence = desc.match(/^(.+?[.!?])(?:\s|$)/s);
  let candidate = firstSentence?.[1]?.trim() ?? desc;
  if (candidate.length <= MAX_SUBTITLE_LEN) return candidate;
  const sliced = candidate.slice(0, MAX_SUBTITLE_LEN);
  const lastSpace = sliced.lastIndexOf(" ");
  candidate = lastSpace > 0 ? sliced.slice(0, lastSpace) : sliced;
  return `${candidate.replace(/[\s.,;:!?-]+$/, "")}…`;
}

export function SystemNode({ data, selected }: { data: SystemNodeData; selected?: boolean }) {
  const { state, dispatch } = useArchViz();
  const aggregatedRisk = data.aggregatedRiskCount ?? 0;
  const riskContainerCount = data.containersWithRiskCount ?? 0;
  const subtitle = subtitleFor(data.system);
  const stage = data.lifecycleStage?.trim();
  return (
    <div
      className={cn(
        "relative rounded-2xl border-2 border-primary/60 bg-primary text-primary-foreground transition-all text-center",
        "px-10 py-6 shadow-lg min-w-[360px] max-w-[420px]",
        "cursor-pointer hover:border-primary",
        selected &&
          "ring-2 ring-offset-2 ring-offset-background ring-[color:var(--node-selection-ring)]",
      )}
    >
      <CommentBadge count={data.commentCount ?? 0} carryOver={data.carryOverCount ?? 0} />
      {state.showRisks && aggregatedRisk > 0 && (
        <RiskBadge
          count={aggregatedRisk}
          className="-top-3 -right-3 left-auto"
          title={`${aggregatedRisk} risk${aggregatedRisk === 1 ? "" : "s"} across ${riskContainerCount} container${riskContainerCount === 1 ? "" : "s"} — click to view`}
          onClick={() => dispatch({ type: "OPEN_RISKS_SHEET" })}
        />
      )}
      {stage && (
        <div className="absolute top-3 left-3 text-[9px] uppercase tracking-eyebrow rounded-full bg-brand-600 text-primary-foreground px-2 py-0.5 font-mono font-medium">
          {stage}
        </div>
      )}
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        className="!bg-primary-foreground/40"
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom"
        className="!bg-primary-foreground/40"
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        className="!bg-primary-foreground/40"
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right"
        className="!bg-primary-foreground/40"
      />
      <div className="flex items-center justify-center gap-2 mb-2">
        <Layers className="text-primary-foreground h-8 w-8" />
      </div>
      <div
        className="text-primary-foreground leading-none tracking-tight"
        style={{ fontSize: "calc(24px * var(--archviz-label-scale, 1))" }}
      >
        {data.system.label}
      </div>
      {subtitle && (
        <p
          className="mt-2 text-primary-foreground/85 leading-snug line-clamp-2"
          style={{ fontSize: "calc(16px * var(--archviz-label-scale, 1))" }}
        >
          {subtitle}
        </p>
      )}
      <div
        className="mt-3 uppercase tracking-eyebrow text-primary-foreground/60"
        style={{ fontSize: "calc(10px * var(--archviz-label-scale, 1))" }}
      >
        {data.containerCount} container{data.containerCount === 1 ? "" : "s"}
        {stage && ` · ${stage} stage`}
      </div>
      <Handle
        type="source"
        position={Position.Top}
        id="top-src"
        className="!bg-primary-foreground/40"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-src"
        className="!bg-primary-foreground/40"
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-src"
        className="!bg-primary-foreground/40"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-src"
        className="!bg-primary-foreground/40"
      />
    </div>
  );
}
